import { useMemo, useState, type ReactNode } from 'react';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useNavigate } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import {
  andFilters,
  formatCivilDate,
  links,
  resolveFormattingLocale,
  useListSearch,
  exportQueryOf,
} from '@eis/core';
import {
  ExportButton,
  InfiniteDataGrid,
  PageHeader,
  PageLayout,
  PropertyFilter,
  RequirePermission,
  StatusChip,
  cardSurfaceSx,
  useGridExportColumns,
  type ExportableGridColDef,
} from '@eis/patterns';
import { billingPermissions as p } from '../../../permissions';
import { isBillingUnavailable, useFilterCatalog, useInfiniteList } from '../api/invoices';
import {
  formatMinor,
  monthRange,
  periodLabel,
  toInvoiceRows,
  type InvoiceRow,
} from '../model/types';
import { BillingActionDialog, type DialogAction } from './BillingDialogs';

/** The invoice catalog's sortable keys, by column. */
const SORT_KEYS: Readonly<Record<string, string>> = {
  number: 'invoiceNumber',
  type: 'type',
  policyCode: 'policyCode',
  periodFrom: 'billingIntervalFrom',
  dueDate: 'dueDate',
  grossMinor: 'grossAmount',
  status: 'status',
};

/**
 * Rechnungen — the tenant's invoices (SDD-015 §7.1, SDD-017 §9).
 *
 * Filtering, search, order and paging are the SERVICE's: the property filter over the invoice
 * catalog (type, status, policy, holder, period, amounts …), the standard search (an invoice
 * number, a policy code, a holder's name), the order by column, and one scroll — the rows arrive
 * page by page by the service's opaque tokens, newest first unless the reader orders otherwise.
 * The month is a quick choice beside it: one civil range (`billingIntervalFrom ≥ first,
 * billingIntervalTo ≤ next first`, §5.3) — a plain month input, not an effective-date picker,
 * because an invoice period is not a belief about a date (CLAUDE.md rule 6). Everything is in the
 * URL, so a filtered list is a link.
 *
 * The dialogs the insurance pages open live here: a link carries `?policy=…&action=…` and the page
 * mounts exactly that dialog, scoped to that policy (backbone §1 — link builders, never a
 * cross-module import).
 */
export function InvoiceListPage({
  policyCode = '',
  action = null,
  onDialogClosed,
}: {
  /** From the route: the policy a dialog is about, and which dialog to open (`links.billing.invoices`). */
  readonly policyCode?: string;
  readonly action?: DialogAction | null;
  readonly onDialogClosed?: () => void;
}): ReactNode {
  const { t, i18n } = useTranslation('billing');
  const locale = resolveFormattingLocale(i18n.language);
  const navigate = useNavigate();
  const url = useListSearch();
  const month = typeof url.raw.month === 'string' ? url.raw.month : '';
  const [dialog, setDialog] = useState<DialogAction | null>(action);

  const search = url.params.search ?? '';
  const range = monthRange(month);
  const catalog = useFilterCatalog('invoices');
  const listFilters = andFilters(
    range === undefined
      ? undefined
      : `billingIntervalFrom>=${range.periodFrom};billingIntervalTo<=${range.periodTo}`,
    // A link about one policy scopes the list to it — unless the reader searches the whole book.
    policyCode === '' || search !== '' ? undefined : `policyCode==${policyCode}`,
    url.params.filters,
  );
  const list = useInfiniteList('invoices', {
    ...url.params,
    filters: listFilters,
    includeTotal: true,
  });
  const rows = useMemo(() => toInvoiceRows(list.items), [list.items]);
  const filtered =
    search !== '' || (url.params.filters ?? '') !== '' || range !== undefined || policyCode !== '';

  // Each column names its field of `Invoice` (SDD-020 §8.1); amounts are cents on the wire and the
  // service writes them as money in the invoice's currency.
  const columns = useMemo<ExportableGridColDef<InvoiceRow>[]>(
    () => [
      {
        field: 'number',
        headerName: t('invoices.columns.number'),
        width: 170,
        exportPath: 'invoiceNumber',
        renderCell: ({ row }) => (
          <span style={{ fontFamily: 'monospace' }}>
            {row.number === '' ? row.code : row.number}
          </span>
        ),
      },
      {
        field: 'type',
        headerName: t('invoices.columns.type'),
        width: 150,
        exportPath: 'type',
        renderCell: ({ row }) => (
          <Chip
            size="small"
            label={t(`invoices.type.${row.type}` as never, { defaultValue: row.type })}
          />
        ),
      },
      {
        field: 'policyCode',
        headerName: t('invoices.columns.policy'),
        width: 160,
        exportPath: 'policyCode',
      },
      {
        field: 'periodFrom',
        headerName: t('invoices.columns.period'),
        flex: 1,
        minWidth: 200,
        // A period is two dates; the file's cell is where it starts (one path, one column).
        exportPath: 'billingIntervalFrom',
        exportType: 'DATE',
        valueGetter: (_value, row) => periodLabel(row.periodFrom, row.periodTo, locale),
      },
      {
        field: 'dueDate',
        headerName: t('invoices.columns.due'),
        width: 130,
        exportPath: 'dueDate',
        exportType: 'DATE',
        valueFormatter: (value: string) => (value === '' ? '—' : formatCivilDate(value, locale)),
      },
      {
        field: 'grossMinor',
        headerName: t('invoices.columns.gross'),
        width: 130,
        exportPath: 'grossAmount',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value: number, row) => formatMinor(value, row.currency, locale),
      },
      {
        field: 'status',
        headerName: t('invoices.columns.status'),
        width: 160,
        exportPath: 'status',
        renderCell: ({ row }) => (
          <StatusChip
            status={row.status}
            label={t(`invoices.status.${row.status}` as never, { defaultValue: row.status })}
          />
        ),
      },
    ],
    [t, locale],
  );
  const exportColumns = useGridExportColumns(columns);

  const closeDialog = (): void => {
    setDialog(null);
    onDialogClosed?.();
  };

  const header = (
    <PageHeader
      title={t('invoices.title')}
      description={t('invoices.subtitle')}
      actions={
        <Stack direction="row" spacing={1}>
          <ExportButton
            service="billing"
            permission={p.exports.create}
            rowCount={list.isLoading ? undefined : rows.length}
            buildRequest={(format) => ({
              collection: 'invoices',
              query: exportQueryOf({ filters: listFilters, search, order: url.params.order }),
              columns: exportColumns,
              format,
              label: t('invoices.title'),
            })}
          />
          <RequirePermission anyOf={[p.billRuns.view]}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                void navigate({ href: links.billing.runs() });
              }}
            >
              {t('invoices.actions.runs')}
            </Button>
          </RequirePermission>
          <RequirePermission anyOf={[p.invoices.create]}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setDialog('schedule');
              }}
            >
              {t('invoices.actions.schedule')}
            </Button>
          </RequirePermission>
          <RequirePermission anyOf={[p.invoices.correct]}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setDialog('correction');
              }}
            >
              {t('invoices.actions.correction')}
            </Button>
          </RequirePermission>
          <RequirePermission anyOf={[p.invoices.create]}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                setDialog('manual');
              }}
            >
              {t('invoices.actions.manual')}
            </Button>
          </RequirePermission>
          <RequirePermission anyOf={[p.invoices.create]}>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon />}
              onClick={() => {
                setDialog('issue');
              }}
            >
              {t('invoices.actions.issue')}
            </Button>
          </RequirePermission>
        </Stack>
      }
    />
  );

  return (
    <PageLayout header={header}>
      {/* The filter, the month and the grid in one table card (design v2, prompt 24). */}
      <Paper variant="outlined" sx={(theme) => ({ p: 2.5, ...cardSurfaceSx(theme) })}>
        <Stack spacing={2}>
          <TextField
            size="small"
            type="month"
            label={t('invoices.month')}
            value={month}
            onChange={(event) => {
              url.set({ month: event.target.value });
            }}
            slotProps={{ inputLabel: { shrink: true } }}
            sx={{ maxWidth: 220 }}
          />
          <PropertyFilter
            catalog={catalog.data}
            value={{ filters: url.params.filters ?? '', search }}
            onChange={(next) => {
              url.set({ filters: next.filters, search: next.search });
            }}
            placeholder={t('invoices.search')}
          />
          <InfiniteDataGrid
            rows={rows}
            list={list}
            columns={columns}
            ariaLabel={t('invoices.ariaLabel')}
            order={url.params.order}
            onOrderChange={(order) => {
              url.set({ order });
            }}
            sortKeys={SORT_KEYS}
            errorDescription={
              isBillingUnavailable(list.error)
                ? t('invoices.error.unavailable')
                : t('invoices.error.list')
            }
            emptyTitle={filtered ? t('invoices.empty.filteredTitle') : t('invoices.empty.title')}
            emptyDescription={
              filtered ? t('invoices.empty.filtered') : t('invoices.empty.description')
            }
            onRowClick={(row) => {
              void navigate({ href: links.billing.invoice(row.code) });
            }}
          />
        </Stack>
      </Paper>
      {dialog !== null && (
        <BillingActionDialog
          key={dialog}
          action={dialog}
          policyCode={policyCode}
          onClose={closeDialog}
        />
      )}
    </PageLayout>
  );
}
